export type LoaderState<T> = {
  data: T | null;
  error: Error | null;
  isLoading: boolean;
  lastLoadedAt: number | null;
};

export type Loader<T> = {
  load(options?: { force?: boolean }): Promise<T>;
  getState(): LoaderState<T>;
  reset(): void;
};

export type CreateLoaderConfig<T> = {
  fetch: () => Promise<T>;
  staleMs?: number;
};

/**
 * Creates a server-data loader with in-flight dedup, staleness, and error transience.
 *
 * Guarantees:
 *   - Concurrent load() calls share one fetch.
 *   - If data was loaded within staleMs, load() returns cached data without network.
 *   - Errors reject all concurrent awaiters and clear the in-flight promise so the
 *     next load() retries cleanly.
 *   - reset() clears data, lastLoadedAt, and error; next load() refetches.
 *
 * Used to back Zustand store "loadX" methods so they don't each re-implement
 * the _loadXPromise pattern. See docs/superpowers/specs/2026-04-13-api-duplicate-dispatch-dedup-design.md.
 */
export function createLoader<T>(config: CreateLoaderConfig<T>): Loader<T> {
  const staleMs = config.staleMs ?? 0;
  let state: LoaderState<T> = {
    data: null,
    error: null,
    isLoading: false,
    lastLoadedAt: null,
  };
  let inflight: Promise<T> | null = null;
  // Incremented by reset() so an in-flight fetch started before reset()
  // can tell it's been invalidated and must NOT write state or clear
  // inflight on completion. Without this, a mutation that calls reset()
  // + optimistic update would be clobbered by the stale fetch resolving
  // with pre-mutation data and a fresh lastLoadedAt.
  let generation = 0;

  const isFresh = (): boolean => {
    if (state.data === null || state.lastLoadedAt === null) return false;
    if (staleMs === 0) return false;
    return Date.now() - state.lastLoadedAt < staleMs;
  };

  const load = (options?: { force?: boolean }): Promise<T> => {
    if (options?.force) {
      // Invalidate any in-flight fetch so its result is discarded, then
      // start a fresh one. Without this, `force: true` would join an
      // existing in-flight request, which is the opposite of what the
      // flag semantically promises.
      generation++;
      inflight = null;
    }
    if (!options?.force && isFresh()) {
      return Promise.resolve(state.data as T);
    }
    if (inflight) return inflight;

    state = { ...state, isLoading: true, error: null };

    const myGen = generation;
    inflight = (async () => {
      try {
        const data = await config.fetch();
        if (generation === myGen) {
          state = {
            data,
            error: null,
            isLoading: false,
            lastLoadedAt: Date.now(),
          };
        }
        return data;
      } catch (e) {
        const error = e instanceof Error ? e : new Error(String(e));
        if (generation === myGen) {
          state = { ...state, isLoading: false, error };
        }
        throw error;
      } finally {
        if (generation === myGen) {
          inflight = null;
        }
      }
    })();

    return inflight;
  };

  const getState = (): LoaderState<T> => state;

  const reset = (): void => {
    generation++;
    state = { data: null, error: null, isLoading: false, lastLoadedAt: null };
    inflight = null;
  };

  return { load, getState, reset };
}
